import React from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import { Users, MessageSquare, Target, UserCheck } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

type Accent = "blue" | "green" | "purple" | "orange" | "red";

const accentStyles: Record<Accent, { iconBg: string; iconFg: string; value: string }> = {
  blue: {
    iconBg: "bg-blue-50 dark:bg-blue-500/15",
    iconFg: "text-[#2563EB] dark:text-[#60A5FA]",
    value: "text-[#2563EB] dark:text-[#60A5FA]",
  },
  green: {
    iconBg: "bg-green-50 dark:bg-green-500/15",
    iconFg: "text-[#16A34A] dark:text-[#22C55E]",
    value: "text-[#16A34A] dark:text-[#22C55E]",
  },
  purple: {
    iconBg: "bg-purple-50 dark:bg-purple-500/15",
    iconFg: "text-[#9333EA] dark:text-[#C084FC]",
    value: "text-[#9333EA] dark:text-[#C084FC]",
  },
  orange: {
    iconBg: "bg-orange-50 dark:bg-orange-500/15",
    iconFg: "text-[#F97316] dark:text-[#FB923C]",
    value: "text-[#F97316] dark:text-[#FB923C]",
  },
  red: {
    iconBg: "bg-red-50 dark:bg-red-500/15",
    iconFg: "text-[#DC2626] dark:text-[#F87171]",
    value: "text-[#DC2626] dark:text-[#F87171]",
  },
};

const AgencyDashboard: React.FC = () => {
  const { t } = useTranslation("agency");
  const { stats, loading, error } = useAgencyData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
          <p className="text-sm text-muted-foreground">{t("dashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#FEF2F2] dark:bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#DC2626] dark:text-[#F87171]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">{t("dashboard.loadError")}</h3>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const statCards: Array<{
    title: string;
    value: number;
    icon: typeof Users;
    accent: Accent;
  }> = [
    {
      title: t("dashboard.totalAccounts"),
      value: stats?.totalAccounts || 0,
      icon: Users,
      accent: "blue",
    },
    {
      title: t("dashboard.activeAccounts"),
      value: stats?.activeAccounts || 0,
      icon: UserCheck,
      accent: "green",
    },
    {
      title: t("dashboard.totalContacts"),
      value: stats?.totalContacts || 0,
      icon: Users,
      accent: "purple",
    },
    {
      title: t("dashboard.totalMessages"),
      value: stats?.totalMessages || 0,
      icon: MessageSquare,
      accent: "orange",
    },
    {
      title: t("dashboard.totalCampaigns"),
      value: stats?.totalCampaigns || 0,
      icon: Target,
      accent: "red",
    },
  ];

  return (
    <div className="w-full">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2 tracking-tight">
          {t("dashboard.title")}
        </h3>
        <p className="text-muted-foreground text-sm">
          {t("dashboard.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {statCards.map((stat) => {
          const style = accentStyles[stat.accent];
          const Icon = stat.icon;
          return (
            <div
              key={stat.title}
              className={clsx(
                "border border-border bg-card rounded-[20px] shadow-sm p-5",
                "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:hover:shadow-none dark:hover:bg-[#3F3F46]/40"
              )}
            >
              <div
                className={clsx(
                  "w-10 h-10 rounded-full flex items-center justify-center mb-4",
                  style.iconBg
                )}
              >
                <Icon className={clsx("w-5 h-5", style.iconFg)} strokeWidth={2} />
              </div>
              <div className="space-y-1">
                <p className={clsx("text-[32px] leading-none font-bold tracking-tight", style.value)}>
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground font-medium">{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 border border-border bg-card rounded-[20px] shadow-sm p-6 transition-colors duration-200">
        <h4 className="text-lg font-semibold text-foreground mb-4 tracking-tight">
          {t("dashboard.info")}
        </h4>
        <div className="space-y-0 text-sm">
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">{t("dashboard.avgContacts")}</span>
            <span className="text-foreground font-medium">
              {stats?.totalAccounts && stats.totalAccounts > 0
                ? Math.round(stats.totalContacts / stats.totalAccounts)
                : 0}
            </span>
          </div>
          <div className="flex justify-between py-3 border-b border-border">
            <span className="text-muted-foreground">{t("dashboard.avgMessages")}</span>
            <span className="text-foreground font-medium">
              {stats?.totalAccounts && stats.totalAccounts > 0
                ? Math.round(stats.totalMessages / stats.totalAccounts)
                : 0}
            </span>
          </div>
          <div className="flex justify-between py-3">
            <span className="text-muted-foreground">{t("dashboard.avgCampaigns")}</span>
            <span className="text-foreground font-medium">
              {stats?.totalAccounts && stats.totalAccounts > 0
                ? Math.round(stats.totalCampaigns / stats.totalAccounts)
                : 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyDashboard;
